import { useRef, useState } from "react";
import { ImagePlus, X, CheckCircle2, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { SITE, SERVICE_CHOICES, REFERRAL_SOURCES, CONTACT_METHODS, TIMEFRAMES } from "../data/site";

const initialForm = {
  name: "",
  phone: "",
  email: "",
  property_location: "",
  service_needed: "",
  preferred_contact: "Phone",
  timeframe: "Flexible / just planning ahead",
  description: "",
  referral_source: "",
};

export const QuoteForm = () => {
  const [form, setForm] = useState(initialForm);
  const [photos, setPhotos] = useState([]); // {path, original_filename, content_type, preview}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [honeypot, setHoneypot] = useState(""); // hidden field — bots fill it, humans never see it
  const [formStartedAt] = useState(() => Date.now());
  const fileInputRef = useRef(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image — skipped.`);
        continue;
      }
      const data = new FormData();
      data.append("file", file);
      try {
        const res = await api.post("/quotes/photos", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) => [
          ...p,
          { ...res.data, preview: URL.createObjectURL(file) },
        ]);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (path) => {
    setPhotos((p) => p.filter((ph) => ph.path !== path));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/quotes", {
        ...form,
        website: honeypot,
        form_started_at: formStartedAt,
        photos: photos.map(({ path, original_filename, content_type }) => ({
          path,
          original_filename,
          content_type,
        })),
      });
      setSubmitted(true);
      // GA4 conversion: fire exactly once, only after a confirmed successful submission
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          service_needed: form.service_needed,
          method: "quote_form",
        });
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white border border-linen p-10 md:p-14 text-center" data-testid="quote-success">
        <CheckCircle2 className="w-14 h-14 text-forest mx-auto" />
        <h2 className="text-3xl sm:text-4xl font-medium text-ink mt-6">Thank you — we've received your request.</h2>
        <p className="mt-4 text-ink/70 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
          A member of the MacWay team will review the details and reach out using your preferred contact method.
          If your situation is time-sensitive, the fastest way to reach us is by phone.
        </p>
        <a href={SITE.phoneHref} className="btn-primary mt-8" data-testid="quote-success-call-button">
          <Phone className="w-4 h-4" />
          Call {SITE.phone}
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-linen p-7 md:p-10" data-testid="quote-form">
      {/* Honeypot: invisible to humans, irresistible to bots */}
      <div style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }} aria-hidden="true">
        <label htmlFor="qf-website">Website</label>
        <input
          id="qf-website"
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="qf-name" className="form-label">
            Your name <span className="text-brass">*</span>
          </label>
          <input id="qf-name" className="form-input" required value={form.name} onChange={set("name")} data-testid="quote-form-name" autoComplete="name" />
        </div>
        <div>
          <label htmlFor="qf-phone" className="form-label">
            Phone number <span className="text-brass">*</span>
          </label>
          <input id="qf-phone" type="tel" className="form-input" required value={form.phone} onChange={set("phone")} data-testid="quote-form-phone" autoComplete="tel" />
        </div>
        <div>
          <label htmlFor="qf-email" className="form-label">Email address</label>
          <input id="qf-email" type="email" className="form-input" value={form.email} onChange={set("email")} data-testid="quote-form-email" autoComplete="email" />
        </div>
        <div>
          <label htmlFor="qf-location" className="form-label">
            Property location <span className="text-brass">*</span>
          </label>
          <input
            id="qf-location"
            className="form-input"
            required
            placeholder="City / neighborhood & state"
            value={form.property_location}
            onChange={set("property_location")}
            data-testid="quote-form-location"
          />
        </div>
        <div>
          <label htmlFor="qf-service" className="form-label">
            Service needed <span className="text-brass">*</span>
          </label>
          <select id="qf-service" className="form-input" required value={form.service_needed} onChange={set("service_needed")} data-testid="quote-form-service">
            <option value="" disabled>Select a service…</option>
            {SERVICE_CHOICES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="qf-contact-method" className="form-label">Preferred contact method</label>
          <select id="qf-contact-method" className="form-input" value={form.preferred_contact} onChange={set("preferred_contact")} data-testid="quote-form-contact-method">
            {CONTACT_METHODS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="qf-timeframe" className="form-label">Project timeframe</label>
          <select id="qf-timeframe" className="form-input" value={form.timeframe} onChange={set("timeframe")} data-testid="quote-form-timeframe">
            {TIMEFRAMES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="qf-referral" className="form-label">How did you hear about us?</label>
          <select id="qf-referral" className="form-input" value={form.referral_source} onChange={set("referral_source")} data-testid="quote-form-referral">
            <option value="">Select one…</option>
            {REFERRAL_SOURCES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label htmlFor="qf-description" className="form-label">Tell us briefly about the situation</label>
          <textarea
            id="qf-description"
            rows={4}
            className="form-input resize-y"
            placeholder="A sentence or two is plenty — the property, the timeline, anything on your mind."
            value={form.description}
            onChange={set("description")}
            data-testid="quote-form-description"
          />
        </div>

        <div className="md:col-span-2">
          <span className="form-label">Photos <span className="font-normal text-ink/50">(optional — a few quick shots help us quote accurately)</span></span>
          <div className="mt-1 flex flex-wrap items-center gap-4">
            {photos.map((ph) => (
              <div key={ph.path} className="relative w-24 h-24 border border-linen overflow-hidden group" data-testid="quote-photo-thumb">
                <img src={ph.preview} alt={ph.original_filename || "Uploaded photo"} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(ph.path)}
                  className="absolute top-1 right-1 bg-ink/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove photo"
                  data-testid="quote-photo-remove"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-24 h-24 border-2 border-dashed border-linen hover:border-forest text-ink/50 hover:text-forest flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-50"
              data-testid="quote-photo-add"
            >
              {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImagePlus className="w-6 h-6" />}
              <span className="text-[0.65rem] font-semibold uppercase tracking-wide">{uploading ? "Uploading" : "Add photos"}</span>
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} data-testid="quote-photo-input" />
          </div>
        </div>
      </div>

      {error && (
        <p className="mt-6 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 px-4 py-3" role="alert" data-testid="quote-form-error">
          {error}
        </p>
      )}

      <div className="mt-8 flex flex-col sm:flex-row items-center gap-5">
        <button type="submit" disabled={submitting || uploading} className="btn-brass w-full sm:w-auto disabled:opacity-60" data-testid="quote-form-submit">
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Sending…
            </>
          ) : (
            "Request a Quote"
          )}
        </button>
        <p className="text-xs text-ink/50 leading-relaxed">
          No pressure, no obligation. We only use these details to respond to your request — see our{" "}
          <a href="/privacy" className="underline hover:text-forest">Privacy Policy</a>. Prefer to talk it
          through? Call us at{" "}
          <a href={SITE.phoneHref} className="font-semibold text-forest hover:underline">{SITE.phone}</a>.
        </p>
      </div>
    </form>
  );
};
